import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ItemCombobox } from "@/components/ItemCombobox";
import { ArrowLeftRight, Plus, X, Save, Printer, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { createKeyboardNav } from "@/lib/keyboardNav";
import { PaginationBar, usePagination } from "@/components/Pagination";

const emptyLine = () => ({ item_id: "", batch_id: "", qty: 1, part_number: "", batch_number: "", available: 0, unit: "" });

export default function StockTransfer() {
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const pg = usePagination(transfers);
  const [mode, setMode] = useState("list"); // "list" | "form"
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), source_store_id: "", dest_store_id: "", narration: "", lines: [emptyLine()] });
  const [batchesByItem, setBatchesByItem] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const formRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [st, it, tr] = await Promise.all([api.get("/stores"), api.get("/stocks"), api.get("/stocks/transfers")]);
      setStores(st.data); setItems(it.data.items || []); setTransfers(tr.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const storeMap = useMemo(() => Object.fromEntries(stores.map(s => [s.id, s.name])), [stores]);
  const itemMap = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);

  const availableAtSource = useCallback(async (item_id) => {
    if (!form.source_store_id || !item_id) return 0;
    try {
      const r = await api.get(`/stocks/store-wise?store_id=${form.source_store_id}`);
      const row = r.data.find(x => x.item_id === item_id);
      return row?.qty || 0;
    } catch { return 0; }
  }, [form.source_store_id]);

  const loadBatches = useCallback(async (item_id) => {
    if (!item_id) return;
    if (batchesByItem[item_id]) return;
    try {
      const r = await api.get(`/items/${item_id}/stock-batches`);
      setBatchesByItem(m => ({ ...m, [item_id]: r.data }));
    } catch { /* ignore */ }
  }, [batchesByItem]);

  const setLine = (idx, patch) => setForm(f => ({ ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((_, i) => i !== idx) : f.lines }));

  const onPickItem = async (idx, itm) => {
    const avail = await availableAtSource(itm.id);
    setLine(idx, { item_id: itm.id, part_number: itm.part_number, unit: itm.unit || "pcs", available: avail, batch_id: "", batch_number: "" });
    loadBatches(itm.id);
  };

  const openNew = () => {
    setForm({ date: new Date().toISOString().slice(0, 10), source_store_id: "", dest_store_id: "", narration: "", lines: [emptyLine()] });
    setBatchesByItem({});
    setSaved(null);
    setMode("form");
  };

  const cancel = () => { setMode("list"); setSaved(null); };

  const save = async () => {
    const lines = form.lines.filter(l => l.item_id && Number(l.qty) > 0)
      .map(l => ({ item_id: l.item_id, qty: Number(l.qty), batch_id: l.batch_id || null, batch_number: l.batch_number || null }));
    if (!form.source_store_id || !form.dest_store_id) return toast.error("From and To stores are required");
    if (form.source_store_id === form.dest_store_id) return toast.error("From and To stores must differ");
    if (lines.length === 0) return toast.error("Add at least one item line");
    setSaving(true);
    try {
      const { data } = await api.post("/stocks/transfer-multi", {
        source_store_id: form.source_store_id,
        dest_store_id: form.dest_store_id,
        date: form.date,
        narration: form.narration,
        lines,
      });
      toast.success(`Transfer ${data.ref} saved`);
      setSaved({ ref: data.ref, id: data.id });
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const handleKeys = useMemo(() => createKeyboardNav({
    fieldSelector: 'input:not([type=hidden]):not([disabled]), select, [role=combobox], button[data-kb-btn]',
    onEnd: (c) => c.querySelector('[data-testid="transfer-save"]')?.focus(),
  }), []);

  // Print current form (for saved transfer)
  const printForm = () => window.print();

  return (
    <div className="p-8 print:p-4">
      {mode === "list" ? (
        <>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><ArrowLeftRight className="w-3 h-3"/> Stocks · Stock Data</div>
              <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Stock Transfer</h1>
              <div className="text-xs text-muted-foreground mt-1">Move stock between stores or godowns</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="new-transfer-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Plus className="w-3 h-3 mr-1"/> New Transfer</Button>
              <Button data-testid="transfer-print-list" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
            </div>
          </div>

          <div className="border border-border bg-card rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="transfers-table">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Ref</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">From</th>
                  <th className="text-left p-3">To</th>
                  <th className="text-right p-3">Lines</th>
                  <th className="text-left p-3">Narration</th>
                </tr>
              </thead>
              <tbody>
                {transfers.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No transfers yet</td></tr>}
                {pg.pageItems.map(t => (
                  <tr key={t.id} data-testid={`transfer-row-${t.id}`} className="border-t border-border hover:bg-secondary/40">
                    <td className="p-3 font-mono text-xs text-primary">{t.ref}</td>
                    <td className="p-3 text-xs text-muted-foreground">{(t.date || t.created_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-xs">{storeMap[t.source_store_id] || "-"}</td>
                    <td className="p-3 text-xs">{storeMap[t.dest_store_id] || "-"}</td>
                    <td className="p-3 text-right font-mono">{t.lines ? t.lines.length : (t.item_id ? 1 : 0)}</td>
                    <td className="p-3 text-xs text-muted-foreground truncate max-w-64">{t.narration || t.notes || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginationBar pg={pg}/>
          </div>
        </>
      ) : (
        <div ref={formRef} onKeyDownCapture={handleKeys}>
          <div className="flex items-end justify-between mb-6 print:mb-3">
            <div>
              <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2 print:hidden"><ArrowLeftRight className="w-3 h-3"/> Stocks · Stock Data</div>
              <h1 className="font-display font-black text-3xl uppercase tracking-tighter">{saved ? "Transfer Saved" : "New Stock Transfer"}</h1>
              {saved && <div className="text-xs text-primary font-mono mt-1">Ref: {saved.ref}</div>}
            </div>
            <div className="flex gap-2 print:hidden">
              {!saved && <Button data-testid="transfer-save" onClick={save} disabled={saving} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Save className="w-3 h-3 mr-1"/> Save</Button>}
              <Button data-testid="transfer-print" variant="outline" onClick={printForm} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
              <Button data-testid="transfer-cancel" variant="outline" onClick={cancel} className="rounded-sm text-xs uppercase tracking-wider"><X className="w-3 h-3 mr-1"/> {saved ? "Close" : "Cancel"}</Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 border border-border bg-card p-4 rounded-sm">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Transfer Date</Label>
              <Input data-testid="transfer-date" type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} disabled={!!saved} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From Store</Label>
              <Select value={form.source_store_id} onValueChange={(v) => { setForm(f => ({ ...f, source_store_id: v, lines: f.lines.map(l => ({ ...l, available: 0 })) })); setBatchesByItem({}); }} disabled={!!saved}>
                <SelectTrigger data-testid="transfer-from" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue placeholder="Pick source"/></SelectTrigger>
                <SelectContent>{stores.filter(s => s.status === "Active" || !s.status).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To Store</Label>
              <Select value={form.dest_store_id} onValueChange={(v) => setForm(f => ({ ...f, dest_store_id: v }))} disabled={!!saved}>
                <SelectTrigger data-testid="transfer-to" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue placeholder="Pick destination"/></SelectTrigger>
                <SelectContent>{stores.filter(s => (s.status === "Active" || !s.status) && s.id !== form.source_store_id).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Narration</Label>
              <Input data-testid="transfer-narration" value={form.narration} onChange={(e) => setForm(f => ({ ...f, narration: e.target.value }))} placeholder="Optional note" disabled={!!saved} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
            </div>
          </div>

          <div className="border border-border bg-card rounded-sm overflow-hidden mb-4">
            <table className="w-full text-sm" data-testid="transfer-lines">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3 w-64">Item Name</th>
                  <th className="text-left p-3">Part No</th>
                  <th className="text-left p-3">Batch No</th>
                  <th className="text-left p-3">Unit</th>
                  <th className="text-right p-3">Available Qty</th>
                  <th className="text-right p-3">Transfer Qty</th>
                  <th className="text-right p-3 w-10 print:hidden"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, idx) => (
                  <tr key={idx} data-kb-row className="border-t border-border align-top">
                    <td className="p-2">
                      <ItemCombobox items={items} value={l.item_id} onSelect={(itm) => onPickItem(idx, itm)}
                        testId={`transfer-item-${idx}`} labelFn={(x) => `${x.item_name} · ${x.part_number}`} triggerClass="h-8"/>
                    </td>
                    <td className="p-2 font-mono text-xs text-primary uppercase">{l.part_number || "-"}</td>
                    <td className="p-2">
                      {(batchesByItem[l.item_id] || []).length > 0 ? (
                        <Select value={l.batch_id || "none"} onValueChange={(v) => {
                          if (v === "none") { setLine(idx, { batch_id: "", batch_number: "" }); return; }
                          const b = (batchesByItem[l.item_id] || []).find(x => x.id === v);
                          setLine(idx, { batch_id: v, batch_number: b?.batch_number || "" });
                        }} disabled={!!saved}>
                          <SelectTrigger data-testid={`transfer-batch-${idx}`} className="rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue placeholder="Any"/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— Any —</SelectItem>
                            {(batchesByItem[l.item_id] || []).map(b => (
                              <SelectItem key={b.id} value={b.id}>{b.batch_number} · Qty {b.qty_remaining}{b.expiry_date ? ` · exp ${b.expiry_date.slice(0,10)}` : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2 text-xs text-muted-foreground">{l.unit || "-"}</td>
                    <td className="p-2 text-right font-mono text-xs">{l.available}</td>
                    <td className="p-2">
                      <Input data-testid={`transfer-qty-${idx}`} type="number" min="0" value={l.qty}
                        onChange={(e) => setLine(idx, { qty: e.target.value })} disabled={!!saved}
                        className="rounded-sm bg-secondary border-border h-8 text-right w-24 ml-auto"/>
                    </td>
                    <td className="p-2 text-right print:hidden">
                      <Button size="sm" variant="ghost" data-kb-btn data-testid={`transfer-line-remove-${idx}`}
                        onClick={() => removeLine(idx)} disabled={!!saved || form.lines.length === 1}
                        className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"><Trash2 className="w-3 h-3"/></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!saved && (
              <div className="p-2 border-t border-border bg-secondary/30 print:hidden">
                <Button variant="ghost" size="sm" data-kb-btn data-testid="transfer-add-line" onClick={addLine} className="rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/> Add Line</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
